"use client"

import { CalendarIcon } from "lucide-react"
import { format } from "date-fns"
import { useState } from "react"
import { Calendar } from "./calendar"
import { Popover, PopoverTrigger, PopoverContent } from "./popover"
import { Button } from "./button"
import { cn } from "../../utils/cn"

interface MonthRange {
    start: Date | undefined
    end: Date | undefined
}

interface MonthRangePickerProps {
    value: MonthRange
    onChange: (range: MonthRange) => void
    className?: string
}

export function MonthRangePicker({ value, onChange, className }: MonthRangePickerProps) {
    const [openPicker, setOpenPicker] = useState<"start" | "end" | null>(null)

    const renderButton = (label: string, openType: "start" | "end", selectedDate?: Date) => (
        <Popover open={openPicker === openType} onOpenChange={(open) => setOpenPicker(open ? openType : null)}>
            <PopoverTrigger asChild>
                <Button
                    variant="default"
                    size="sm"
                    className={cn(
                        "justify-start text-left w-[200px]",
                        !selectedDate && "text-muted-foreground"
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "MMMM yyyy") : label}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" sideOffset={4} className="p-2 w-45">
                <Calendar
                    mode="single"
                    captionLayout="dropdown"
                    selected={selectedDate}
                    defaultMonth={selectedDate ?? new Date()}
                    onSelect={(date) => {
                        if (!date) return
                        const isStart = openType === "start"

                        const updated = isStart
                            ? {
                                start: new Date(date.getFullYear(), date.getMonth(), 1),
                                end: value.end
                            }
                            : {
                                start: value.start,
                                end: new Date(date.getFullYear(), date.getMonth() + 1, 0) // last day of month
                            }

                        // Validation
                        if (!isStart && updated.start !== undefined && updated.end !== undefined && updated.end < updated.start) {
                            updated.start = undefined
                        }
                        if (isStart && updated.start !== undefined && updated.end !== undefined && updated.start > updated.end) {
                            updated.end = undefined
                        }
                        onChange(updated)
                        setOpenPicker(null)
                    }}
                    onMonthChange={(month) => {
                        const isStart = openType === "start"
                        const updated = isStart
                            ? {
                                start: new Date(month.getFullYear(), month.getMonth(), 1),
                                end: value.end
                            }
                            : {
                                start: value.start,
                                end: new Date(month.getFullYear(), month.getMonth() + 1, 0) // last day of month
                            }

                        if (!isStart && updated.start !== undefined && updated.end !== undefined && updated.end < updated.start) {
                            updated.start = undefined
                        }
                        if (isStart && updated.start !== undefined && updated.end !== undefined && updated.start > updated.end) {
                            updated.end = undefined
                        }
                        onChange(updated)
                    }}
                    components={{
                        Day: () => <span className="hidden" />,
                        Weekdays: () => <span className="hidden" />,
                        Nav: () => <span className="hidden" />,
                    }}
                    startMonth={new Date(2024, 0, 1)}
                    endMonth={new Date(new Date().getFullYear(), 11, 31)}
                    className="remove-days month-year-picker"
                    classNames={{
                        caption_label: "hidden",
                        dropdowns: "flex justify-center items-center gap-4 w-full mt-2 text-foreground bg-background",
                    }}
                />
            </PopoverContent>
        </Popover>
    )

    return (
        <div className={cn("grid gap-2 font-semibold", className)}>
            <div className="flex gap-2">
                {renderButton("Start", "start", value.start)}
                {renderButton("End", "end", value.end)}
            </div>
        </div>
    )
}
